import { action } from "mobx";
import { Texture } from "../../../texture";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";

export type ImageAdjustRectFilters = {
  u_avgR: number;
  u_avgG: number;
  u_avgB: number;
  u_autoWhiteBalance: number;
  u_temperature: number;
  u_temperatureRange: number;
  u_opacity: number;
  u_brightness: number;
  u_contrast: number;
  u_hue: number;
  u_saturation: number;
  u_sharpness: number;
  u_levelsTone: number;
  u_levelsBrightness: number;
};

export type ImageAdjustRectComponentState = {
  filters: ImageAdjustRectFilters;
  texture: Texture;
};

export class ImageAdjustRectComponent extends Webgl2Component<
  undefined,
  ImageAdjustRectComponentState
> {
  constructor(texture: Texture, filters?: Partial<ImageAdjustRectFilters>) {
    super({
      texture,
      filters: {
        u_avgR: 1.0,
        u_avgG: 1.0,
        u_avgB: 1.0,
        u_autoWhiteBalance: 0,
        u_temperature: 0,
        u_temperatureRange: 200.0,
        u_opacity: 1,
        u_brightness: 0,
        u_contrast: 0,
        u_hue: 0,
        u_saturation: 0,
        u_sharpness: 0,
        u_levelsTone: 0,
        u_levelsBrightness: 0,
        ...filters,
      },
    });
    this.transform.setSize(texture.textureSize);
  }

  public resetFilters(filters?: Partial<ImageAdjustRectFilters>) {
    this.state.filters = {
      u_avgR: this.state.filters.u_avgR,
      u_avgG: this.state.filters.u_avgG,
      u_avgB: this.state.filters.u_avgB,
      u_autoWhiteBalance: this.state.filters.u_autoWhiteBalance,
      u_temperature: 0,
      u_temperatureRange: 200.0,
      u_opacity: 1,
      u_brightness: 0,
      u_contrast: 0,
      u_hue: 0,
      u_saturation: 0,
      u_sharpness: 0,
      u_levelsTone: 0,
      u_levelsBrightness: 0,
      ...filters,
    };
  }

  public getRenderData() {
    const material = Webgl2.materials.texture;
    material.setTexture("u_texture0", this.state.texture);
    material.setUniforms(this.state.filters);
    return { material };
  }

  public calculateWhiteBalanceGainsForTextureSource() {
    const video = this.state.texture.textureSource;

    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    const { width, height } = Webgl2.canvas;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;
    context.drawImage(video, 0, 0);
    const pixels = context.getImageData(0, 0, width, height).data;
    let sumR = 0,
      sumG = 0,
      sumB = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      sumR += r;
      sumG += g;
      sumB += b;
    }
    const numPixels = pixels.length / 4;
    const avgR = sumR / numPixels;
    const avgG = sumG / numPixels;
    const avgB = sumB / numPixels;
    const average = (avgR + avgG + avgB) / 3.0;
    const gain_R = average / avgR;
    const gain_G = average / avgG;
    const gain_B = average / avgB;
    return { gain_R, gain_G, gain_B };
  }

  public calculateWhiteBalanceGains(pixels: Uint8Array | Uint8ClampedArray): {
    gain_R: number;
    gain_G: number;
    gain_B: number;
  } {
    let sumR = 0,
      sumG = 0,
      sumB = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      sumR += pixels[i];
      sumG += pixels[i + 1];
      sumB += pixels[i + 2];
    }

    const numPixels = pixels.length / 4;
    const avgR = sumR / numPixels;
    const avgG = sumG / numPixels;
    const avgB = sumB / numPixels;
    const average = (avgR + avgG + avgB) / 3.0;
    const gain_R = average / avgR;
    const gain_G = average / avgG;
    const gain_B = average / avgB;

    return { gain_R, gain_G, gain_B };
  }

  public calculateWhiteBalanceGainsFromWebGL() {
    let pixels = null;
    if (this.state.texture.textureSize.width == 0) {
      const { width, height } = Webgl2.canvas;
      pixels = new Uint8Array(width * height * 4);
      Webgl2.gl.readPixels(0, 0, width, height, Webgl2.gl.RGBA, Webgl2.gl.UNSIGNED_BYTE, pixels);
    } else {
      pixels = this.state.texture.readTexture().data;
    }

    return this.calculateWhiteBalanceGains(pixels);
  }

  public calculateWhiteBalanceGainsFromVideoSource() {
    let pixels = null;
    const video = this.state.texture.textureSource;
    if (video) {
      const canvas = document.createElement("canvas");
      const { width, height } = Webgl2.canvas;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d") as CanvasRenderingContext2D;
      context.drawImage(video, 0, 0);
      pixels = context.getImageData(0, 0, width, height).data;
    } else {
      pixels = this.state.texture.readTexture().data;
    }

    return this.calculateWhiteBalanceGains(pixels);
  }

  public checkWhiteBalance(): boolean {
    return this.state.filters["u_autoWhiteBalance"] > 0;
  }

  public autoWhiteBalance() {
    const isAuto = this.checkWhiteBalance();
    if (isAuto) {
      const { gain_R, gain_G, gain_B } = this.calculateWhiteBalanceGainsFromVideoSource();
      // eslint-disable-next-line no-console
      console.log(gain_R, gain_G, gain_B, "calculateWhiteBalanceGainsFromVideoSource");
      this.state.filters["u_avgR"] = gain_R;
      this.state.filters["u_avgB"] = gain_B;
      this.state.filters["u_avgG"] = gain_G;
    }
  }

  @action
  public isAutoWhiteBalance(isAuto: boolean) {
    this.state.filters["u_autoWhiteBalance"] = isAuto ? 1 : 0;
    this.autoWhiteBalance();
  }
}

export default ImageAdjustRectComponent;
